/**
 * useUsers — data layer for the Admin Portal People tab.
 *
 * Wraps the seven user-management API endpoints in React Query:
 *   - One list query (`getUsers`) keyed on `userKeys.list()` so every caller
 *     (PeopleTab, AdminPortal score box, nav badge) shares one cache entry
 *     and one network round-trip.
 *   - Six mutations — role / org / tenant-assign / tenant-remove / deactivate /
 *     welcome-email — each invalidating `userKeys.all` on success so the next
 *     read reconciles against the server. We deliberately don't optimistically
 *     update like `useOrganizations.ts` does: the user-list rows are
 *     denormalized server-side (computed `accessibleTenantCount`, joined
 *     `organizationName`, `isPlatformAdmin`) and reproducing that derivation
 *     on the client risks drift. Invalidate-and-refetch keeps the cache
 *     honest at the cost of one extra round-trip per admin action.
 *
 * Pending-count derivation is a pure function (`deriveUserPendingState`)
 * exported alongside the hook so the nav-badge code and the unit tests can
 * pin behavior without spinning up a React renderer. "Pending" here means
 * the welcome email has never been sent (`welcomeEmailSentAt === null`).
 * An `undefined` value is treated as unknown, not pending — see the test
 * file for the explicit boundary case.
 */

import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'

/* -------------------------------------------------------------------------- */
/* Query-key factory                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Keys exported so callers (the nav badge, the page) can invalidate or read
 * the cache from outside the hook. Mirrors `orgKeys` in `useOrganizations.ts`
 * and `complianceKeys` in `useComplianceData.ts`.
 */
export const userKeys = {
  all: ['users'] as const,
  list: () => [...userKeys.all, 'list'] as const,
  detail: (id: string) => [...userKeys.all, 'detail', id] as const,
}

/* -------------------------------------------------------------------------- */
/* Public types                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Row shape from `GET /usermanagement`. Mirrors the `User` interface that
 * `UserManagement.tsx:19-36` defined locally — the page is being replaced by
 * the Admin Portal People tab and the type lives here now so multiple
 * consumers (drawer, score box, badge) share one definition.
 *
 * `organizationId` / `organizationName` can be null for legacy rows whose
 * `Users.OrganizationId` predates the multi-tenant MSP Phase 1 backfill.
 * `welcomeEmailSentAt` is null when never sent and ISO string otherwise.
 */
export interface UserDto {
  id: string
  objectId: string
  email: string
  displayName: string
  role: string
  isActive: boolean
  createdAt: string
  lastLoginAt: string
  welcomeEmailSentAt?: string | null
  accessibleTenantCount: number
  accessibleTenantIds: string[]
  organizationId: string | null
  organizationName: string | null
  isPlatformAdmin: boolean
}

export interface SendWelcomeEmailResult {
  success: boolean
  message: string
  sentTo: string
  sentAt: string
}

export interface UseUsersResult {
  /** List rows. Empty array (never undefined) once the first fetch resolves. */
  users: UserDto[]
  /** True while the initial fetch is in flight. */
  isLoading: boolean
  /** True during a background refetch (after first paint). */
  isFetching: boolean
  isError: boolean
  error?: Error
  /** Pending-welcome count — drives the People score box "needs hand" verdict. */
  pendingCount: number
  refetch: () => void

  // Mutations — each returns a stable handle so callers wire their own
  // success/error toasts without re-wrapping the React Query API.
  updateUserRole: (args: { objectId: string; role: string }) => Promise<unknown>
  isUpdatingRole: boolean
  updateUserOrganization: (args: { objectId: string; organizationId: string }) => Promise<void>
  isUpdatingOrganization: boolean
  assignUserToTenant: (args: { objectId: string; tenantId: string }) => Promise<unknown>
  isAssigningTenant: boolean
  removeUserFromTenant: (args: { objectId: string; tenantId: string }) => Promise<void>
  isRemovingTenant: boolean
  deactivateUser: (objectId: string) => Promise<unknown>
  isDeactivating: boolean
  sendWelcomeEmail: (args: { objectId: string }) => Promise<SendWelcomeEmailResult>
  isSendingWelcomeEmail: boolean
  /** Atomic grant/revoke of Platform Admin (sets org + role together server-side). */
  setPlatformAdmin: (args: { objectId: string; granted: boolean }) => Promise<void>
  isSettingPlatformAdmin: boolean
}

/* -------------------------------------------------------------------------- */
/* Hook                                                                       */
/* -------------------------------------------------------------------------- */

const USER_STALE_MS = 30 * 1000

export function useUsers(): UseUsersResult {
  const queryClient = useQueryClient()
  const listKey = userKeys.list()

  const listQuery = useQuery({
    queryKey: listKey,
    // `api.getUsers()` is typed as `Promise<any[]>` (see api.ts:838); narrow
    // at the boundary so the rest of the app sees `UserDto[]`.
    queryFn: () => api.getUsers() as Promise<UserDto[]>,
    staleTime: USER_STALE_MS,
  })

  const { pendingCount } = useMemo(
    () => deriveUserPendingState(listQuery.data),
    [listQuery.data],
  )

  /* ---------- Mutations ---------- */

  // All six mutations share the same invalidation policy: on success refetch
  // the list so the server-denormalized fields (tenant count, org name,
  // platform-admin flag, welcome-email timestamp) reconcile correctly.

  const invalidateAll = () => queryClient.invalidateQueries({ queryKey: userKeys.all })

  const updateRoleMutation = useMutation({
    mutationFn: ({ objectId, role }: { objectId: string; role: string }) =>
      api.updateUserRole(objectId, role),
    onSuccess: invalidateAll,
  })

  const updateOrgMutation = useMutation({
    mutationFn: ({ objectId, organizationId }: { objectId: string; organizationId: string }) =>
      api.updateUserOrganization(objectId, organizationId),
    onSuccess: invalidateAll,
  })

  const assignTenantMutation = useMutation({
    mutationFn: ({ objectId, tenantId }: { objectId: string; tenantId: string }) =>
      api.assignUserToTenant(objectId, tenantId),
    onSuccess: invalidateAll,
  })

  const removeTenantMutation = useMutation({
    mutationFn: ({ objectId, tenantId }: { objectId: string; tenantId: string }) =>
      api.removeUserFromTenant(objectId, tenantId),
    onSuccess: invalidateAll,
  })

  const deactivateMutation = useMutation({
    mutationFn: (objectId: string) => api.deactivateUser(objectId),
    onSuccess: invalidateAll,
  })

  const welcomeEmailMutation = useMutation({
    mutationFn: ({ objectId }: { objectId: string }) =>
      api.sendWelcomeEmail(objectId) as Promise<SendWelcomeEmailResult>,
    onSuccess: invalidateAll,
  })

  const setPlatformAdminMutation = useMutation({
    mutationFn: ({ objectId, granted }: { objectId: string; granted: boolean }) =>
      api.setPlatformAdmin(objectId, granted),
    onSuccess: invalidateAll,
  })

  return {
    users: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    isFetching: listQuery.isFetching,
    isError: listQuery.isError,
    error: listQuery.error as Error | undefined,
    pendingCount,
    refetch: () => { void listQuery.refetch() },

    updateUserRole: (args) => updateRoleMutation.mutateAsync(args),
    isUpdatingRole: updateRoleMutation.isPending,
    updateUserOrganization: (args) =>
      updateOrgMutation.mutateAsync(args).then(() => undefined),
    isUpdatingOrganization: updateOrgMutation.isPending,
    assignUserToTenant: (args) => assignTenantMutation.mutateAsync(args),
    isAssigningTenant: assignTenantMutation.isPending,
    removeUserFromTenant: (args) =>
      removeTenantMutation.mutateAsync(args).then(() => undefined),
    isRemovingTenant: removeTenantMutation.isPending,
    deactivateUser: (objectId) => deactivateMutation.mutateAsync(objectId),
    isDeactivating: deactivateMutation.isPending,
    sendWelcomeEmail: (args) => welcomeEmailMutation.mutateAsync(args),
    isSendingWelcomeEmail: welcomeEmailMutation.isPending,
    setPlatformAdmin: (args) =>
      setPlatformAdminMutation.mutateAsync(args).then(() => undefined),
    isSettingPlatformAdmin: setPlatformAdminMutation.isPending,
  }
}

/* -------------------------------------------------------------------------- */
/* Pure helpers — exported for unit tests                                     */
/* -------------------------------------------------------------------------- */

/**
 * Count users whose welcome email has never been sent.
 *
 * Pending policy (load-bearing — change in lock-step with the tests):
 *   - `welcomeEmailSentAt === null`  → pending (admin action available).
 *   - `welcomeEmailSentAt === undefined` → unknown / field absent on legacy
 *     rows; NOT counted as pending. The admin UI can show a neutral state
 *     instead of nagging about a state we don't actually know.
 *   - Any string value → already sent, not pending.
 *
 * Pulled out of the hook so the test file can exercise it without invoking
 * React (mirrors the `derivePendingState` pattern in `useOrganizations.ts`).
 */
export function deriveUserPendingState(
  users?: readonly UserDto[],
): { pendingCount: number } {
  if (!users || users.length === 0) return { pendingCount: 0 }
  const pendingCount = users.reduce(
    (n, u) => (u.welcomeEmailSentAt === null ? n + 1 : n),
    0,
  )
  return { pendingCount }
}

/**
 * A user is a "half-granted admin" when they hold the GlobalAdmin role but are
 * NOT a platform admin — the grant was half-applied (GlobalAdmin on a non-Platform
 * org). This is the "GlobalAdmin who sees nothing" shape (the marc.rolfes
 * incident): the Container's all-tenants short-circuit fires on Role alone, but
 * every cross-tenant edge gate requires the full (Platform-org, GlobalAdmin)
 * tuple — so the user can sign in yet see no tenant data.
 *
 * Note: `isPlatformAdmin` on the list row is the server's TUPLE-ONLY value
 * (OrganizationId == Platform AND Role == GlobalAdmin); it does NOT account for
 * the config break-glass allowlist. So a break-glass admin whose DB row still
 * sits on a non-Platform org reads as half-granted here until the server's login
 * self-heal aligns the org. That's acceptable for a diagnostic flag: the DB row
 * genuinely is inconsistent, and it self-clears on that admin's next sign-in.
 *
 * Pulled out as a pure function so the People list, the drawer, and the unit
 * tests share one definition without spinning up a React renderer.
 */
export function isHalfGrantedAdmin(
  user: Pick<UserDto, 'role' | 'isPlatformAdmin'>,
): boolean {
  return user.role === 'GlobalAdmin' && !user.isPlatformAdmin
}
